import React from 'react';
import { startOfWeek, addDays, isSameDay, format, isToday as isDateToday } from 'date-fns';
import AppointmentCard from './AppointmentCard';

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am-7pm
const HOUR_HEIGHT = 60; // px per hour
const START_HOUR = 7;

function layoutOverlaps(appts) {
  // Sort by start time, then by duration (longer first)
  const sorted = [...appts].sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const columns = [];

  for (const appt of sorted) {
    let placed = false;
    for (let col = 0; col < columns.length; col++) {
      const lastInCol = columns[col][columns[col].length - 1];
      if (appt.start >= lastInCol.end) {
        columns[col].push(appt);
        appt._col = col;
        placed = true;
        break;
      }
    }
    if (!placed) {
      appt._col = columns.length;
      columns.push([appt]);
    }
  }

  const totalCols = columns.length || 1;
  for (const appt of sorted) {
    appt._totalCols = totalCols;
  }
  return sorted;
}

function getPosition(appt) {
  const startMin = appt.start.getHours() * 60 + appt.start.getMinutes();
  const endMin = appt.end.getHours() * 60 + appt.end.getMinutes();
  const top = ((startMin - START_HOUR * 60) / 60) * HOUR_HEIGHT;
  const height = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 24);
  const col = appt._col || 0;
  const totalCols = appt._totalCols || 1;
  const width = `calc(${100 / totalCols}% - 2px)`;
  const left = `calc(${(col / totalCols) * 100}% + 1px)`;
  return { top, height, width, left };
}

export default function WeekView({ date, appointments, onSelectAppointment, typesMap = {} }) {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Current time indicator
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nowTop = ((nowMin - START_HOUR * 60) / 60) * HOUR_HEIGHT;
  const showNowLine = nowMin >= START_HOUR * 60 && nowMin <= (START_HOUR + HOURS.length) * 60;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex border-b border-strong sticky top-0 z-[5] bg-secondary">
        <div className="w-[50px] md:w-[64px] flex-shrink-0" />
        {days.map(day => {
          const today = isDateToday(day);
          return (
            <div key={day.toISOString()} className={`flex-1 text-center py-2 flex flex-col items-center gap-0.5 border-l border-theme ${today ? 'bg-accent-glow' : ''}`}>
              <span className="text-[10px] font-semibold text-muted-c uppercase tracking-wider">{format(day, 'EEE')}</span>
              <span className={`text-[15px] font-bold w-8 h-8 flex items-center justify-center rounded-full ${today ? 'bg-accent text-white' : 'text-primary-c'}`}>
                {format(day, 'd')}
              </span>
            </div>
          );
        })}
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-y-auto overflow-x-hidden">
        {/* Time column */}
        <div className="w-[50px] md:w-[64px] flex-shrink-0 relative" style={{ height: HOURS.length * HOUR_HEIGHT }}>
          {HOURS.map(h => (
            <div key={h} className="absolute right-0 pr-2 md:pr-3 text-[10px] font-medium text-muted-c" style={{ top: (h - START_HOUR) * HOUR_HEIGHT - 6 }}>
              {h === 0 ? '12 AM' : h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`}
            </div>
          ))}
        </div>

        {/* Day columns */}
        {days.map(day => {
          const dayAppts = layoutOverlaps(appointments.filter(a => isSameDay(a.start, day)));
          const today = isDateToday(day);

          return (
            <div key={day.toISOString()} className="flex-1 relative border-l border-theme" style={{ height: HOURS.length * HOUR_HEIGHT }}>
              {/* Hour grid lines */}
              {HOURS.map(h => (
                <div key={h} className="absolute left-0 right-0 border-b border-theme" style={{ top: (h - START_HOUR) * HOUR_HEIGHT, height: HOUR_HEIGHT }}>
                  {/* Half-hour line */}
                  <div className="absolute left-0 right-0 border-b border-theme/30" style={{ top: HOUR_HEIGHT / 2 }} />
                </div>
              ))}

              {/* Current time line */}
              {today && showNowLine && (
                <div className="absolute left-0 right-0 z-[3] flex items-center" style={{ top: nowTop }}>
                  <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444] -ml-[5px]" />
                  <div className="flex-1 h-[2px] bg-[#EF4444]" />
                </div>
              )}

              {/* Appointments */}
              <div className="absolute inset-0 z-[2]">
                {dayAppts.map(appt => {
                  const pos = getPosition(appt);
                  return (
                    <div
                      key={appt.id}
                      className="absolute overflow-hidden"
                      style={{ top: pos.top, height: pos.height, width: pos.width, left: pos.left }}
                    >
                      <AppointmentCard appointment={appt} compact onClick={onSelectAppointment} typesMap={typesMap} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
